/**
 * Προαιρετικός λόγος για κέρασμα, έκπτωση και διόρθωση στο ταμείο τραπεζιών (απόφαση ιδιοκτήτη
 * 6/10/2026: καταγράφεται ΠΟΙΟΣ το έκανε — ο συνδεδεμένος χρήστης — και, αν θέλει, ΓΙΑΤΙ· χωρίς
 * PIN). Έτοιμοι λόγοι σε κουμπιά + «Άλλο» με σύντομο ελεύθερο κείμενο (κι αυτό προαιρετικό).
 * Δεύτερο πάτημα στον ίδιο λόγο τον ξεεπιλέγει.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

export type StaffReasonKey = 'regular' | 'complaint' | 'kitchenError' | 'delay' | 'other';

export interface StaffReasonChoice {
  key: StaffReasonKey | null;
  /** Το κείμενο του «Άλλο». */
  other: string;
}

export const NO_STAFF_REASON: StaffReasonChoice = { key: null, other: '' };

export const STAFF_REASON_KEYS: StaffReasonKey[] = ['regular', 'complaint', 'kitchenError', 'delay', 'other'];

/** Μήκος του «Άλλο» — σύντομο (ο server κρατά ως 200). */
export const STAFF_REASON_MAX = 100;

const DEFAULT_LABELS: Record<StaffReasonKey, string> = {
  regular: 'Πελάτης σπιτιού',
  complaint: 'Παράπονο',
  kitchenError: 'Λάθος κουζίνας',
  delay: 'Καθυστέρηση',
  other: 'Άλλο',
};

/** Το κείμενο που στέλνεται στον server: '' = κανένας λόγος. «Άλλο» χωρίς κείμενο = «Άλλο». */
export function useStaffReasonText(): (choice: StaffReasonChoice) => string {
  const { t } = useTranslation(['dineIn']);
  return (choice: StaffReasonChoice) => {
    if (!choice.key) return '';
    if (choice.key === 'other') {
      const typed = choice.other.replace(/\s+/g, ' ').trim();
      if (typed) return typed;
    }
    return t(`dineIn:staffReasons.${choice.key}`, { defaultValue: DEFAULT_LABELS[choice.key] });
  };
}

interface StaffReasonPickerProps {
  value: StaffReasonChoice;
  onChange: (value: StaffReasonChoice) => void;
  /** Επικεφαλίδα· προεπιλογή «Λόγος (προαιρετικός)». */
  label?: string;
  isMobile?: boolean;
}

const StaffReasonPicker: React.FC<StaffReasonPickerProps> = ({ value, onChange, label, isMobile }) => {
  const { t } = useTranslation(['dineIn']);
  const title = label ?? t('dineIn:staffReasons.title', { defaultValue: 'Λόγος (προαιρετικός)' });

  return (
    <div role="group" aria-label={title} data-testid="staff-reason-picker">
      <p className="text-sm font-semibold text-t3 mb-2">{title}</p>
      <div className="flex flex-wrap gap-2">
        {STAFF_REASON_KEYS.map(key => {
          const active = value.key === key;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(active ? NO_STAFF_REASON : { key, other: key === 'other' ? value.other : '' })}
              className={`cashier-choice rounded-full border text-sm font-semibold transition-colors ${isMobile ? 'px-4 py-2.5' : 'px-3 py-1.5'} ${
                active
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-surface-2 border-line-strong text-t2 hover:border-blue-500'
              }`}
            >
              {t(`dineIn:staffReasons.${key}`, { defaultValue: DEFAULT_LABELS[key] })}
            </button>
          );
        })}
      </div>
      {value.key === 'other' && (
        <input
          type="text"
          value={value.other}
          maxLength={STAFF_REASON_MAX}
          autoFocus
          onChange={e => onChange({ key: 'other', other: e.target.value })}
          aria-label={t('dineIn:staffReasons.otherLabel', { defaultValue: 'Άλλος λόγος' })}
          placeholder={t('dineIn:staffReasons.otherPlaceholder', { defaultValue: 'Γράψτε τον λόγο (προαιρετικό)' })}
          className={`mt-2 w-full px-3 rounded-lg border border-line-strong bg-surface-2 text-t1 text-sm placeholder-t3 focus:outline-none focus:border-blue-500 ${isMobile ? 'py-3' : 'py-2'}`}
        />
      )}
    </div>
  );
};

export default StaffReasonPicker;
